// ─────────────────────────────────────────────────────────────────────────────
// THIRD-PARTY DATA FLOW — PRIVACY NOTICE
// This drawer syncs wishlist contents and the Shopify `customer_id` to the
// external service below (matat.io). Before any identifier leaves the browser,
// `wishlistCanSync()` checks the Shopify Customer Privacy API
// (`userCanBeTracked()`) so the call is suppressed when the visitor has not
// consented. REQUIRED: this data flow (vendor: matat.io, data: customer_id +
// wishlist product references) must be disclosed in the store privacy policy
// and covered by a Data Processing Agreement. See wishlistCanSync() below.
// ─────────────────────────────────────────────────────────────────────────────
const wishlist_api = 'https://shopify-wishlist.matat.io';

function escHtml(str) {
  return String(str ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#x27;');
}

function safeUrl(url) {
  const s = String(url ?? '').trim();
  // Absolute http(s) URLs (e.g. https://shop.com/products/foo)
  if (/^https?:\/\//i.test(s)) return s;
  // Root-relative paths (e.g. /products/foo) — Shopify returns product URLs in
  // this form. Reject protocol-relative URLs (//evil.com) which start with //.
  if (/^\/(?!\/)/.test(s)) return s;
  return '#';
}

// Respect the visitor's tracking consent before sending any identifier to the
// third-party wishlist service. When the Customer Privacy API is unavailable
// (consent not configured on the store) we allow the first-party functional sync.
function wishlistCanSync() {
  try {
    const cp = window.Shopify && window.Shopify.customerPrivacy;
    if (cp && typeof cp.userCanBeTracked === 'function') {
      return cp.userCanBeTracked();
    }
  } catch (e) {}
  return true;
}

function checkIfProductExistInWishlist(productId) {
  const storedData = localStorage.getItem('wishlist_items');
  if (!storedData) return false;
  return JSON.parse(storedData).some(product => product.id === productId);
}

class WishlistDrawer extends HTMLElement {
  constructor() {
    super();
    if (typeof customer_id !== 'undefined') {
      this.syncWithServer();
      this.syncWishlistToServer();
      this.renderContentFromSessionStorage();
    }
    this.createUniqueUserID();
    if (localStorage.getItem('visitCount')) {
      let visitCount = parseInt(localStorage.getItem('visitCount'));
      visitCount++;
      if (visitCount % 5 === 0) {
        this.syncWishlistToServer();
        visitCount = 0;
      }
      localStorage.setItem('visitCount', visitCount.toString());
    } else {
      localStorage.setItem('visitCount', '1');
    }
    this.renderContentFromSessionStorage();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#WishlistDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  syncWithServer() {
    if (!wishlistCanSync()) return;
    const userId = localStorage.getItem('userId');
    if (userId === customer_id) return;
    fetch(`${wishlist_api}/api/wishlist/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, customer_id }),
    })
      .then(response => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.json();
      })
      .then(() => localStorage.setItem('userId', customer_id))
      .catch(() => {});
  }

  createUniqueUserID() {
    if (!localStorage.getItem('userId')) {
      const userId = (window.crypto && typeof window.crypto.randomUUID === 'function')
        ? window.crypto.randomUUID()
        : Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('userId', userId);
      return userId;
    }
  }

  syncWishlistToServer() {
    if (!wishlistCanSync()) return;
    const userId = localStorage.getItem('userId');
    fetch(`${wishlist_api}/api/wishlist/${userId}`, {
      headers: { 'Content-Type': 'application/json' },
    })
      .then(response => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.json();
      })
      .then(data => {
        const get_items = JSON.parse(localStorage.getItem('wishlist_items') || '[]');
        if (get_items.length > 0) {
          localStorage.setItem('wishlist_items', JSON.stringify(get_items));
        } else {
          localStorage.setItem('wishlist_items', JSON.stringify(data));
        }
        this.renderContentFromSessionStorage();
      })
      .catch(() => {});
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#wishlist-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    setTimeout(() => { this.classList.add('animate', 'active'); });
    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = document.getElementById('WishlistDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });
    if (document.querySelector('#Details-menu-drawer-container').hasAttribute('open')) {
      document.querySelector('#Details-menu-drawer-container').removeAttribute('open');
      document.querySelector('#Details-menu-drawer-container').classList.remove('menu-opening');
      document.querySelector('.header__icon--menu').setAttribute('aria-expanded', false);
      document.body.classList.remove('overflow-hidden-tablet');
      document.querySelector('.section-header').classList.remove('menu-open');
    }
    if (document.querySelector('.header__search details').hasAttribute('open')) {
      document.querySelector('.header__search details').removeAttribute('open');
      document.querySelector('.section-header').classList.remove('menu-open');
    }
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  getPlaceholderSvg() {
    return "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 525 525'%3E%3Crect width='525' height='525' fill='%23f0f0f0'/%3E%3Cpath d='M218.3 232.8h88.4v59.5h-88.4z' fill='%23d4d4d4'/%3E%3Ccircle cx='247' cy='235' r='15' fill='%23d4d4d4'/%3E%3Cpath d='M218.3 292.3l25-30 20 25 30-40 35 45H218.3z' fill='%23d4d4d4'/%3E%3C/svg%3E";
  }

  renderWishlistItem(product) {
    const img = document.createElement('img');
    img.width = 150;
    img.height = 200;
    img.classList.add('attachment-woocommerce_thumbnail', 'size-woocommerce_thumbnail', 'lazyload');
    img.alt = product.title ? escHtml(product.title) : 'Product';
    img.decoding = 'async';
    img.setAttribute('src', product.src && product.src.trim() !== '' ? product.src : this.getPlaceholderSvg());
    img.setAttribute('sizes', '(max-width: 320px) 100vw, 320px');

    const anchor = document.createElement('a');
    anchor.href = safeUrl(product.url);
    anchor.classList.add('wishlist-product-wrap');
    anchor.appendChild(img);

    const imageWrap = document.createElement('div');
    imageWrap.classList.add('wishlist-image-item');
    imageWrap.appendChild(anchor);

    let sale_badge = '';
    let sold_badge = '';
    let tag_badge = '';

    const saleScheme = (window.themeSettings && window.themeSettings.saleBadgeColorScheme) ? ` color-${window.themeSettings.saleBadgeColorScheme}` : '';
    const soldScheme = (window.themeSettings && window.themeSettings.soldOutBadgeColorScheme) ? ` color-${window.themeSettings.soldOutBadgeColorScheme}` : '';

    if (product.compare_price && product.compare_price !== '' && product.compare_price !== product.price) {
      if (window.themeSettings && window.themeSettings.saleBadgeDesign === 'percent_off') {
        const price = parseFloat(product.price.replace(/[^0-9.]/g, ''));
        const comparePrice = parseFloat(product.compare_price.replace(/[^0-9.]/g, ''));
        if (comparePrice > price) {
          const percent = Math.round(((comparePrice - price) / comparePrice) * 100);
          const template = window.themeSettings.salePercentText || '__PERCENT__% OFF';
          sale_badge = `<span class="badge-matat badge sale off-label${saleScheme}">${template.replace('__PERCENT__', percent + '%')}</span>`;
        } else {
          sale_badge = `<span class="badge-matat badge sale off-label${saleScheme}">Sale</span>`;
        }
      } else {
        sale_badge = `<span class="badge-matat badge sale off-label${saleScheme}">Sale</span>`;
      }
    }

    if (product.soldBadge) sold_badge = `<span class="badge-matat badge out-of-stock off-label${soldScheme}">${escHtml(product.soldBadge)}</span>`;
    if (product.tagBadge) tag_badge = `<span class="badge-matat badge off-label new_badge${soldScheme}">${escHtml(product.tagBadge)}</span>`;

    const pro_badges = (sale_badge || sold_badge || tag_badge)
      ? `<div class="wishlist-pro-badges">${sale_badge} ${sold_badge} ${tag_badge}</div>`
      : '';

    const pro_compare_price = (product.compare_price && product.compare_price !== undefined)
      ? `<span class="sale-wishlist-price amount">${escHtml(String(product.compare_price))}</span>`
      : '';

    const hasMultipleVariants = product.has_only_default_variant == 'false' || product.has_only_default_variant == 0 || product.has_only_default_variant == false;
    const pUrl = safeUrl(product.url);
    const addToCartBtn = hasMultipleVariants
      ? `<a href="${pUrl}" class="btn-add-cart demo">${escHtml(wishlist.choose_option)}</a>`
      : `<add-to-cart href="${pUrl}" class="btn-add-cart demo" data-variant-id="${escHtml(product.variantId)}" data-id="${escHtml(product.id)}" role="button" tabindex="0">${escHtml(wishlist.add_to_cart)}</add-to-cart><form method="post" action="/cart/add" accept-charset="UTF-8" class="form" enctype="multipart/form-data" novalidate="novalidate" data-type="add-to-cart-form"><input type="hidden" name="form_type" value="product"><input type="hidden" name="utf8" value="✓"><input type="hidden" name="id" value="${escHtml(product.variantId)}" class="product-variant-id"></form>`;

    return `
      <wishlist-item class="product-col">
        <div class="product-item">
          <div class="img-wrap">
            <wishlist-item-delete-button class="btn-remove-wishlist" data-id="${escHtml(product.id)}" role="button" tabindex="0">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="icon icon-remove"><path d="M10 11v6"/><path d="M14 11v6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
            </wishlist-item-delete-button>
            ${imageWrap.outerHTML}
            ${addToCartBtn}
            ${pro_badges}
          </div>
          <div class="product_info_wrapper">
            <h3 class="product-name">
              <a href="${pUrl}">${escHtml(product.title)}</a>
            </h3>
            <div class="product_inner_info">
              <div class="product-price">
                ${pro_compare_price}
                <span class="woocommerce-Price-amount amount"><bdi>${escHtml(String(product.price))}</bdi></span>
              </div>
              <div class="go_to_product">
                <a href="${pUrl}">${escHtml(wishlist.choose_option)}</a>
              </div>
            </div>
          </div>
        </div>
      </wishlist-item>`;
  }

  renderContentFromSessionStorage() {
    const storedData = JSON.parse(localStorage.getItem('wishlist_items') || '[]');
    const wishlistItemsElement = document.querySelector('wishlist-items');
    if (storedData.length >= 1) {
      wishlistItemsElement.innerHTML = storedData.map(product => this.renderWishlistItem(product)).join('\n');
      this.updateWishlistBubble();
    }
    document.querySelectorAll('wishlist-button').forEach(button => {
      button.classList.toggle('has_in_wishlist', checkIfProductExistInWishlist(button.dataset.id));
    });
  }

  updateWishlistBubble() {
    const storedData = localStorage.getItem('wishlist_items');
    const productData = JSON.parse(storedData);
    const count = productData.length;
    const layout = count >= 1
      ? `<span aria-hidden="true">${count}</span><span class="visually-hidden">${count} Items</span>`
      : '';
    this.setDrawerStatus(count >= 1);
    document.querySelector('#WishlistDrawer .drawer__heading span').innerHTML = `(${count})`;
    document.querySelector('#wishlist-bubble-container').innerHTML = layout;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  setDrawerStatus(status) {
    if (status) {
      this.classList.remove('wishlist-empty');
    } else {
      setTimeout(() => { this.classList.add('wishlist-empty'); }, 500);
    }
  }

  removeProductFromSessionStorage(productIdToRemove) {
    const storedData = localStorage.getItem('wishlist_items');
    if (storedData) {
      const updated = JSON.parse(storedData).filter(product => product.id !== productIdToRemove);
      localStorage.setItem('wishlist_items', JSON.stringify(updated));
      this.updateWishlistBubble();
    }
  }
}
customElements.define('wishlist-drawer', WishlistDrawer);

class WishlistItems extends HTMLElement {}
customElements.define('wishlist-items', WishlistItems);

class WishlistItem extends WishlistItems {}
customElements.define('wishlist-item', WishlistItem);

class WishlistDeleteButton extends WishlistItem {
  constructor() {
    super();
    this.addEventListener('click', this.removeItem);
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.removeItem();
      }
    });
  }

  removeItem() {
    const product_id = this.dataset.id;
    this.sendDeleteRequest(product_id);
    document.querySelector('wishlist-drawer').removeProductFromSessionStorage(product_id);
    this.fadeAndRemove(this.closest('wishlist-item'));
    document.querySelectorAll('wishlist-button').forEach(button => {
      button.classList.toggle('has_in_wishlist', checkIfProductExistInWishlist(button.dataset.id));
    });
  }

  fadeAndRemove(el) {
    let opacity = 1;
    const interval = setInterval(() => {
      if (opacity > 0) {
        opacity -= 0.1;
        el.style.opacity = opacity;
      } else {
        clearInterval(interval);
        el.style.display = 'none';
        el.remove();
      }
    }, 25);
  }

  sendDeleteRequest(itemId) {
    fetch(`${wishlist_api}/api/wishlist`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: localStorage.getItem('userId'), product_id: itemId }),
    })
      .then(response => {
        if (!response.ok) throw new Error('Network response was not ok');
      })
      .catch(() => {});
  }
}
customElements.define('wishlist-item-delete-button', WishlistDeleteButton);

class WishlistButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.handleButtonClicked);
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.handleButtonClicked();
      }
    });
    if (checkIfProductExistInWishlist(this.dataset.id)) {
      this.classList.add('has_in_wishlist');
    }
  }

  handleButtonClicked() {
    const prod = this.getProductObject(this.dataset);
    if (this.classList.contains('has_in_wishlist')) {
      this.classList.remove('has_in_wishlist');
      this.removeFromWishlist(prod);
    } else {
      this.classList.add('has_in_wishlist');
      this.addToWishlist(prod);
      setTimeout(() => { this.openDrawer(); }, 200);
    }
    document.querySelector('wishlist-drawer').renderContentFromSessionStorage();
  }

  addToWishlist(prod) {
    this.appendJsonToSessionStorage(prod);
    this.sendWishlistItem(prod);
  }

  removeFromWishlist(prod) {
    document.querySelector('wishlist-drawer').removeProductFromSessionStorage(prod.id);
    document.querySelector('wishlist-item-delete-button').sendDeleteRequest(prod.id);
  }

  openDrawer() {
    document.querySelector('wishlist-drawer').open();
  }

  getProductObject(prod) {
    return {
      has_only_default_variant: prod.has_only_default_variant,
      id: prod.id,
      title: prod.title,
      price_varies: prod.price_varies,
      price: prod.price,
      price_max: prod.price_max,
      price_min: prod.price_min,
      src: prod.src,
      url: prod.url,
      variantId: prod.variantId,
      compare_price: prod.compareprice,
      saleBadge: prod.salebadge,
      soldBadge: prod.soldbadge,
      tagBadge: prod.tagbadge,
    };
  }

  appendJsonToSessionStorage(jsonData) {
    const existingData = localStorage.getItem('wishlist_items');
    if (existingData) {
      const parsedData = JSON.parse(existingData);
      parsedData.push(jsonData);
      localStorage.setItem('wishlist_items', JSON.stringify(parsedData));
    } else {
      localStorage.setItem('wishlist_items', JSON.stringify([jsonData]));
    }
  }

  sendWishlistItem(prod) {
    if (!wishlistCanSync()) return;
    const userId = localStorage.getItem('userId');
    fetch(`${wishlist_api}/api/wishlist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, ...prod }),
    })
      .then(response => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.json();
      })
      .catch(() => {});
  }
}
customElements.define('wishlist-button', WishlistButton);